import { Component, OnInit, Input, SimpleChanges, Output, EventEmitter, forwardRef } from '@angular/core';
import { 
    IonGrid, IonRow, IonCol, IonSpinner, ToastController
} from '@ionic/angular/standalone';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import moment from 'moment';

import { MarvButtonComponent, MarvMessageComponent } from '@marv/components';
import { QuestionnaireComponent, ValidateQuestionnaireEvent } from '@app/components';
import { StoreCheckService, UpdateStoreCheckObjectiveDto } from '@app/services';
import { StoreCheck, StoreCheckObjective, StoreCheckInputModel } from '@app/models';

@Component({
    selector: 'app-permanent-planning-questionnaire',
    templateUrl: './questionnaire.component.html',
    styleUrls: ['./questionnaire.component.scss'],
    imports: [
        IonGrid, IonRow, IonCol, IonSpinner, TranslatePipe, 
        forwardRef(() => MarvButtonComponent), forwardRef(() => MarvMessageComponent), forwardRef(() => QuestionnaireComponent)
    ]
})
export class PermanentPlanningQuestionnaireComponent  implements OnInit {

    @Input({alias : 'operationId', required : true}) operationId: number;
    @Input({alias : 'storeCheckId', required : true}) storeCheckId: number;
    @Input({alias : 'objectiveId', required : false}) objectiveId: number;
    @Input({alias : 'displayAll', required : false}) displayAll: boolean = true;
    @Output() onChangeQuestionnaire = new EventEmitter<boolean>();
    
    isFetching: boolean = false;
    isFetchingSave: boolean = false;
    disabledQuestionnaire: boolean = false;
    questionnaireUpdated: boolean = false;
    storeCheck : StoreCheck|null = null;
    
    objectives: Array<StoreCheckObjective> = [];
    objectivesStatus: Map<number, boolean> = new Map();
    currentObjId: number|null = null;
    
    model: StoreCheckInputModel|null = null;
    inputsFieldsValues: Map<string, any> = new Map();
    
    constructor(
        public translateService: TranslateService,
        private toastController: ToastController,
        private storeCheckService: StoreCheckService
    ) { }

    ngOnInit() {
        
    }
    
    async ngOnChanges(changes: SimpleChanges) {
        if(changes['storeCheckId']){
            await this.loadStoreCheck();
        }
        else if(changes['objectiveId'] || changes['displayAll']){
            await this.setObjectives();
        }        
    }
    
    async loadStoreCheck(){
        
        this.isFetching = true;
        this.storeCheck = null;  
        this.disabledQuestionnaire = true;
        
        if(this.storeCheckId && this.operationId){
            let storeCheck = await this.storeCheckService.loadStoreCheck(this.operationId, this.storeCheckId);
            if(storeCheck){
                this.storeCheck = storeCheck;  
                this.disabledQuestionnaire = (this.storeCheck.allowToUpdateSC !== true);        
            }
        }        
         
        await this.setObjectives(); 
         
        this.isFetching = false;
    }
    
    async setObjectives(){
        
        this.currentObjId = null;
        this.model = null;
        this.objectives = [];
        this.inputsFieldsValues.clear();
        this.objectivesStatus.clear();
        this.questionnaireUpdated = false;
        
        if(this.storeCheck && this.storeCheck.objectives?.length > 0){ 
            let existObjective = (this.objectiveId ? this.storeCheck.objectives.find(o => o.id === this.objectiveId) : undefined);
            if(!this.displayAll && existObjective){
                this.objectives = [existObjective];
                this.objectivesStatus.set(existObjective.id, false);
            }
            else{
                this.objectives = this.storeCheck.objectives;
                this.storeCheck.objectives.forEach(obj => {
                    this.objectivesStatus.set(obj.id, false);
                });
            }
            this.currentObjId = (existObjective ? existObjective.id : this.storeCheck.objectives[0].id);     
            await this.loadValues();           
        }
    }
    
    async loadValues(){
        this.inputsFieldsValues.clear();
        let values = await this.storeCheckService.loadStoreCheckObjectivesValues(this.operationId, this.storeCheckId);
        values.forEach(v => {
            this.inputsFieldsValues.set(v.key, v.value);
        });
        this.setUpRules();
    }
    
    setUpRules() {
        this.model = this.storeCheck?.scims?.filter(scim => scim.objective === this.currentObjId)[0] || null;        
    }
    
    getObjectivesStatusIndex(): number{
        return (this.objectives.findIndex(o => o.id === this.currentObjId) + 1);
    }
    
    getNextObjectives(objId: number): number|null{
        let newObjId: number|null = null;
        let index = this.objectives.findIndex(o => o.id === objId);
        if(index !== -1){
            let nextObjective = this.objectives[(index+1)%this.objectives.length];
            if(nextObjective.id !== this.currentObjId){             
                if(this.objectivesStatus.get(nextObjective.id)){
                    newObjId = this.getNextObjectives(nextObjective.id);
                }
                else{
                    newObjId = nextObjective.id;
                }
            }
        }        
        return newObjId;
    }
    
    async onValidateQuestionnaire(event: ValidateQuestionnaireEvent) {
        if(event.status){
            if(this.storeCheck && this.currentObjId
                && event.values && event.values.length > 0){
                
                let data: UpdateStoreCheckObjectiveDto = {
                    scId: this.storeCheck.id,
                    storeId: -1,
                    objId: this.currentObjId,
                    salesRep: this.storeCheck.salesRepId,
                    values: event.values
                };
                
                let status = await this.storeCheckService.saveStoreCheckObjective(this.operationId, data);
                if(status){
                    
                    this.questionnaireUpdated = true;
                    this.objectivesStatus.set(this.currentObjId, true);
                    
                    if(this.displayAll){
                        this.currentObjId = this.getNextObjectives(this.currentObjId);
                        if(this.currentObjId === null){
                            this.onChangeQuestionnaire.emit(this.questionnaireUpdated);
                        }
                        else{
                            this.model = null;
                            await this.loadValues();
                        }
                    }
                    else{
                        this.onChangeQuestionnaire.emit(this.questionnaireUpdated);
                    }
                    
                    const toast = await this.toastController.create({
                        message: this.translateService.instant('MSG_SUCCESS_SAVE_STORECHECK_QUESTIONNAIRE'),
                        duration: 4000,
                        position: 'top',
                        color: 'success'
                    });
                    await toast.present();
                    
                }
                else{
                    const toast = await this.toastController.create({
                        message: this.translateService.instant('MSG_ERROR_SAVE_STORECHECK_QUESTIONNAIRE'),
                        duration: 6000,
                        position: 'top',
                        color: 'danger'
                    });
                    await toast.present();
                }
            }            
        }
        else{
            this.onChangeQuestionnaire.emit(this.questionnaireUpdated);
        }
    }
    
}
